import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { Col, Row } from "reactstrap";
import { useHibobApi } from "../../../Integrations/HiBob/api";
import {
	INSTALL_PATH,
	getDisconnectErrorKey,
	getDisconnectReasonKey,
	getSyncErrorKey,
	getSyncStatus,
} from "../../../Integrations/HiBob/messages";
import AccessCard from "./AccessCard";
import ConnectionCard from "./ConnectionCard";
import DisconnectModal from "./DisconnectModal";
import EmptyState from "./EmptyState";
import Header from "./Header";
import Skeleton from "./Skeleton";
import SummaryTiles from "./SummaryTiles";
import SyncCard from "./SyncCard";
import TimeoffCard from "./TimeoffCard";
import { toMoment, useFormats } from "./format";
import { useHibobSettings } from "./useHibobSettings";
import "./style.scss";

function HiBob() {
	const { t } = useTranslation();
	const formats = useFormats();
	const { startSync, disconnect } = useHibobApi();
	const { state, data, reload, watchSync } = useHibobSettings();

	const [syncing, setSyncing] = useState(false);
	const [retryAt, setRetryAt] = useState(null);
	const [refreshing, setRefreshing] = useState(false);
	const [openStep, setOpenStep] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [disconnecting, setDisconnecting] = useState(false);
	const [disconnectError, setDisconnectError] = useState(null);
	const [, setTick] = useState(0);

	const sync = data?.sync;
	const running = sync?.lastStatus === "running";

	const availableAt = useMemo(() => {
		const times = [toMoment(sync?.manualSyncAvailableAt), toMoment(retryAt)].filter(Boolean);
		const latest = times.length ? moment.max(times) : null;
		return latest && latest.isAfter(moment()) ? latest : null;
	}, [sync?.manualSyncAvailableAt, retryAt]);

	// Re-render once the server's cool-down has passed, so the button comes back.
	useEffect(() => {
		if (!availableAt) {
			return undefined;
		}
		const timer = setTimeout(
			() => setTick((n) => n + 1),
			Math.min(availableAt.diff(moment()) + 500, 2 ** 31 - 1),
		);
		return () => clearTimeout(timer);
	}, [availableAt]);

	const onSyncNow = useCallback(async () => {
		setSyncing(true);
		try {
			const response = await startSync();
			if (!response) {
				return;
			}
			const { status, data: body } = response;
			if (status === 202) {
				toast.success(t("hibob-sync-started"));
				watchSync();
				await reload({ silent: true });
			} else if (status === 429) {
				const at = toMoment(body?.retryAt);
				setRetryAt(at ? at.toISOString() : null);
				toast.error(t(getSyncErrorKey("rate_limited"), { time: at ? formats.when(at) : "" }));
			} else if (status === 409) {
				toast.error(t(getSyncErrorKey(body?.reason)));
				await reload({ silent: true });
			} else if (status === 403) {
				toast.error(t("hibob-error-forbidden"));
			} else {
				toast.error(t("hibob-error-generic"));
			}
		} catch (err) {
			toast.error(t("hibob-error-generic"));
		} finally {
			setSyncing(false);
		}
	}, [startSync, t, watchSync, reload, formats]);

	const onRefresh = useCallback(async () => {
		setRefreshing(true);
		try {
			await reload({ silent: true });
		} finally {
			setRefreshing(false);
		}
	}, [reload]);

	const onToggleStep = useCallback((step) => {
		setOpenStep((current) => (current === step ? null : step));
	}, []);

	const openDisconnect = useCallback(() => {
		setDisconnectError(null);
		setModalOpen(true);
	}, []);

	const onDisconnect = useCallback(async () => {
		setDisconnecting(true);
		setDisconnectError(null);
		try {
			const response = await disconnect();
			if (!response) {
				return;
			}
			const { status, data: body } = response;
			if (status === 200) {
				setModalOpen(false);
				toast.success(t("hibob-disconnected-toast"));
				await reload();
			} else if (status === 409 && body?.reason === "not_connected") {
				setModalOpen(false);
				await reload();
			} else if (status === 403) {
				setDisconnectError(t("hibob-error-forbidden"));
			} else {
				setDisconnectError(t(getDisconnectErrorKey(status === 409 ? body?.reason : null)));
			}
		} catch (err) {
			setDisconnectError(t("hibob-error-generic"));
		} finally {
			setDisconnecting(false);
		}
	}, [disconnect, t, reload]);

	let content;
	if (state === "loading") {
		content = <Skeleton />;
	} else if (state === "error") {
		content = (
			<div role="alert" className="hibob-alert is-danger d-flex align-items-center flex-wrap">
				<span className="mr-3">{t("hibob-load-error")}</span>
				<button type="button" className="btn btn-sm btn-secondary ml-auto" onClick={() => reload()}>
					{t("hibob-retry")}
				</button>
			</div>
		);
	} else if (data.status !== "connected" && data.status !== "disconnected") {
		content = <EmptyState />;
	} else {
		const connected = data.status === "connected";
		const disconnectedAt = toMoment(data.disconnectedAt);
		const liveStatus = getSyncStatus(sync?.lastStatus);

		content = (
			<>
				<Header
					status={data.status}
					reason={data.disconnectReason}
					showActions={connected}
					running={running}
					syncing={syncing}
					availableAt={availableAt}
					formats={formats}
					onSyncNow={onSyncNow}
					onDisconnect={openDisconnect}
				/>

				{!connected && (
					<div className="hibob-alert is-warning d-flex align-items-center flex-wrap" data-testid="hibob-disconnected-banner">
						<FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" aria-hidden="true" />
						<span className="mr-3">
							<strong>{t("hibob-banner-disconnected")}</strong>
							{" · "}
							{t(getDisconnectReasonKey(data.disconnectReason))}
							{disconnectedAt && ` · ${formats.date(disconnectedAt)}`}
							<span className="d-block text-sm">{t("hibob-disconnected-text")}</span>
						</span>
						<Link className="btn btn-sm btn-primary ml-lg-auto mt-2 mt-lg-0" to={INSTALL_PATH}>
							{t("hibob-reconnect")}
						</Link>
					</div>
				)}

				<SummaryTiles
					data={data}
					formats={formats}
					onShowEmployeeIssues={() => setOpenStep("users")}
				/>

				<Row>
					<Col lg="8">
						<SyncCard
							data={data}
							formats={formats}
							openStep={openStep}
							onToggleStep={onToggleStep}
							onRefresh={onRefresh}
							refreshing={refreshing}
							paused={!connected}
						/>
					</Col>
					<Col lg="4">
						<AccessCard scopes={data.scopes} />
					</Col>
				</Row>

				{connected && <TimeoffCard writeback={data.writeback} />}

				{connected && <ConnectionCard onDisconnect={openDisconnect} />}

				<div className="sr-only" role="status" aria-live="polite">
					{running
						? t("hibob-sync-live-running")
						: liveStatus
						? t("hibob-sync-status-live", { status: t(liveStatus.key) })
						: ""}
				</div>

				<DisconnectModal
					isOpen={modalOpen}
					loading={disconnecting}
					error={disconnectError}
					onCancel={() => setModalOpen(false)}
					onConfirm={onDisconnect}
				/>
			</>
		);
	}

	return <div className="hibob-page">{content}</div>;
}

export default HiBob;
